import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  SentimentVeryDissatisfied,
  SentimentNeutral,
  SentimentVerySatisfied,
  TaskAlt,
  Close,
  RateReview,
} from "@mui/icons-material";
import { getToken } from "config/storageController";
import { axiosInstanceAuth } from "config/axios-config";
import { snackbar } from "components/awesome_snackbar/snackbar";

// Matches the staff dashboard's info gradient
const BTN_BG = "linear-gradient(195deg, #49a3f1, #1A73E8)";
const BTN_SHADOW = "0 4px 14px rgba(26,115,232,0.38)";
const BTN_BG_HV = "linear-gradient(195deg, #2e8ee0, #1558c0)";

const RATINGS = [
  { key: "bad", Icon: SentimentVeryDissatisfied, label: "Bad", color: "#EF4444" },
  { key: "decent", Icon: SentimentNeutral, label: "Decent", color: "#F59E0B" },
  { key: "love", Icon: SentimentVerySatisfied, label: "Love It", color: "#1A73E8" },
];

const CardWrap = ({ children }) => (
  <div
    style={{
      width: 300,
      background: "#fff",
      borderRadius: 16,
      boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
      overflow: "hidden",
    }}
  >
    {children}
  </div>
);
CardWrap.propTypes = { children: PropTypes.node.isRequired };

const ActionBtn = ({ onClick, disabled, loading, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "11px 0",
        background: disabled ? "#d1d5db" : hovered ? BTN_BG_HV : BTN_BG,
        boxShadow: disabled ? "none" : BTN_SHADOW,
        color: "#fff",
        border: "none",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.02em",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.18s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {loading ? (
        <span
          style={{
            width: 14,
            height: 14,
            border: "2px solid rgba(255,255,255,0.4)",
            borderTopColor: "#fff",
            borderRadius: "50%",
            display: "inline-block",
            animation: "spin 0.7s linear infinite",
          }}
        />
      ) : null}
      {children}
    </button>
  );
};
ActionBtn.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const DISMISSED_KEY = "feedback_widget_dismissed";

const StaffFeedbackWidget = () => {
  const [visible, setVisible] = useState(() => {
    try {
      return !localStorage.getItem(DISMISSED_KEY);
    } catch {
      return true;
    }
  });
  const [rating, setRating] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("rate"); // "rate" | "expand" | "done"

  const token = getToken();
  if (!token) return null;

  const handleRatingClick = (key) => {
    setRating(key);
    setStep("expand");
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const chosen = RATINGS.find((r) => r.key === rating);
      const prefix = chosen ? chosen.label : "";
      const body = text.trim();
      const feedback_content = body ? `${prefix} — ${body}` : prefix;
      const axiosAuth = axiosInstanceAuth().axiosInstance;
      const { data } = await axiosAuth.post("/system-feedback", {
        feedback_type: "staff",
        feedback_content,
      });
      if (data?.success) {
        setStep("done");
      } else {
        snackbar("error", data?.message || "Failed to send feedback.");
      }
    } catch (err) {
      snackbar("error", err?.response?.data?.message || "Failed to send feedback.");
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {}
    setVisible(false);
  };

  const handleDone = () => {
    dismiss();
    setTimeout(() => {
      setStep("rate");
      setRating(null);
      setText("");
    }, 300);
  };

  /* ── Collapsed tab ── */
  if (!visible) {
    return (
      <>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <button
          onClick={() => setVisible(true)}
          style={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 9500,
            background: BTN_BG,
            boxShadow: BTN_SHADOW,
            color: "#fff",
            border: "none",
            borderRadius: 24,
            padding: "9px 18px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <RateReview style={{ fontSize: 16 }} />
          Feedback
        </button>
      </>
    );
  }

  return (
    <>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ position: "fixed", bottom: 32, right: 32, zIndex: 9500 }}>
        {step === "done" ? (
          /* ── Thank You ── */
          <CardWrap>
            <div style={{ padding: "32px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: 14,
                  background: "linear-gradient(135deg, #e8f0fe, #d2e3fc)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 18,
                }}
              >
                <TaskAlt style={{ fontSize: 30, color: "#1A73E8" }} />
              </div>
              <p style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 700, color: "#111" }}>
                Thank you
              </p>
              <p style={{ margin: "0 0 24px", fontSize: 13, color: "#6b7280", lineHeight: 1.65 }}>
                The feedback helps us improve. We appreciate the time you took to share it!
              </p>
              <ActionBtn onClick={handleDone} disabled={false} loading={false}>
                Done
              </ActionBtn>
            </div>
          </CardWrap>
        ) : (
          /* ── Rate / Expand ── */
          <CardWrap>
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 16px 12px",
              }}
            >
              <span
                style={{ fontSize: 13, fontWeight: 700, color: "#111", letterSpacing: "0.01em" }}
              >
                Rate your experience
              </span>
              <button
                onClick={dismiss}
                aria-label="Close"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  padding: 2,
                  borderRadius: 4,
                }}
              >
                <Close style={{ fontSize: 18 }} />
              </button>
            </div>

            {/* Rating pills */}
            <div style={{ display: "flex", gap: 7, padding: "0 16px 14px" }}>
              {RATINGS.map(({ key, Icon, label, color }) => {
                const selected = rating === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleRatingClick(key)}
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 4,
                      padding: "8px 4px",
                      border: `1.5px solid ${selected ? color : "#e5e7eb"}`,
                      borderRadius: 12,
                      background: selected ? `${color}12` : "#fafafa",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    <Icon
                      style={{
                        fontSize: 26,
                        color: selected ? color : "#9ca3af",
                        transition: "color 0.15s",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: selected ? color : "#9ca3af",
                        letterSpacing: "0.03em",
                        transition: "color 0.15s",
                      }}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Expanded section */}
            {step === "expand" && (
              <div style={{ padding: "0 16px 16px" }}>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Tell us more (optional)"
                  rows={3}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 13,
                    color: "#374151",
                    resize: "none",
                    outline: "none",
                    fontFamily: "inherit",
                    marginBottom: 12,
                    lineHeight: 1.55,
                    transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#1A73E8")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
                <ActionBtn onClick={handleSubmit} disabled={loading} loading={loading}>
                  {loading ? "Sending…" : "Submit your feedback"}
                </ActionBtn>
              </div>
            )}
          </CardWrap>
        )}
      </div>
    </>
  );
};

export default StaffFeedbackWidget;
